import React from 'react';

import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';

import {events} from '@edna/utils';

import {EEvent} from '../types';
import Item from './Item';
import * as S from './style';
import {ENotificationType, TNotification} from './types';

export type {TNotification};
export {ENotificationType};

type TProps = {
  className?: string;
};

const DUPLICATE_IGNORE_DURATION = 1_000;

type TNotificationOptions = Omit<TNotification, 'id' | 'createdAt'>;

const Notifications = React.memo<TProps>(({className}) => {
  const [notifications, setNotifications] = React.useState<TNotification[]>([]);

  const addNotification = React.useCallback(
    (notification: TNotificationOptions) => {
      const isDuplicate = notifications.some(
        (item) =>
          item.title === notification.title &&
          item.message === notification.message &&
          item.messageKey === notification.messageKey &&
          isEqual(item.messageArgs, notification.messageArgs) &&
          Date.now() - item.createdAt < DUPLICATE_IGNORE_DURATION,
      );

      if (isDuplicate) {
        return;
      }

      const newNotification = {
        ...notification,
        id: uniqueId(),
        createdAt: Date.now(),
      };

      setNotifications((prevNotifications) => [...prevNotifications, newNotification]);
    },
    [notifications],
  );

  const removeNotification = React.useCallback(
    (id: TNotification['id']) =>
      setNotifications((prevNotifications) =>
        prevNotifications.filter(({id: notificationId}) => notificationId !== id),
      ),
    [],
  );

  React.useEffect(() => {
    events.on(EEvent.ADD_NOTIFICATION, addNotification);

    return () => {
      events.off(EEvent.ADD_NOTIFICATION, addNotification);
    };
  }, [addNotification]);

  return (
    <S.Notifications className={className}>
      {notifications.map((item) => (
        <Item {...item} key={item.id} removeNotification={() => removeNotification(item.id)} />
      ))}
    </S.Notifications>
  );
});

export default Notifications;

export const showNotification = (notification: TNotificationOptions) =>
  events.trigger(EEvent.ADD_NOTIFICATION, notification);
